import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import QRPreview from "@/components/QRPreview";
import PhoneMockup from "@/components/PhoneMockup";
import ScanResult from "@/components/ScanResult";
import DestinationForm, { Input, Field } from "@/components/DestinationForm";
import VCardForm from "@/components/VCardForm";
import DesignStudio from "@/components/DesignStudio";
import { DESTINATIONS, destMeta, DEFAULT_DESIGN, buildEncodedData } from "@/lib/qr";
import { downloadQR, printQR } from "@/lib/download";
import api, { apiError } from "@/lib/api";

const STEPS = ["Destination", "Type", "Content", "Design"];

export default function Builder() {
  const nav = useNavigate();
  const { id } = useParams();
  const { can } = useAuth();
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null);
  const [mode, setMode] = useState("dynamic");
  const [vcardMode, setVcardMode] = useState("updateable");
  const [name, setName] = useState("");
  const [data, setData] = useState({});
  const [design, setDesign] = useState({ ...DEFAULT_DESIGN });
  const [folder, setFolder] = useState("");
  const [tagText, setTagText] = useState("");
  const tags = tagText.split(",").map((t) => t.trim()).filter(Boolean);
  const [company, setCompany] = useState(null);
  const [record, setRecord] = useState(null);
  const [preview, setPreview] = useState("qr");
  const [presets, setPresets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [typeCfg, setTypeCfg] = useState({});

  useEffect(() => { api.get("/presets").then(({ data }) => setPresets(data)).catch(() => {}); api.get("/company").then(({ data }) => setCompany(data)).catch(() => {}); api.get("/qr-types").then(({ data }) => setTypeCfg(data.types || {})).catch(() => {}); }, []);

  const currentPlan = company?.plan || "basic";
  const [lockedTypes, setLockedTypes] = useState(null);
  useEffect(() => { api.get("/company/features").then(({ data: f }) => setLockedTypes(f.locked_types || [])).catch(() => {}); }, []);
  const liveDests = useMemo(() => {
    return DESTINATIONS.filter((d) => !d.hidden).map((d) => {
      const c = typeCfg[d.id] || {};
      const plans = c.plans || [];
      return {
        ...d,
        name: c.display_name || d.name,
        desc: c.description || d.desc,
        icon: c.icon || d.icon,
        featured: c.featured ?? !!d.featured,
        enabled: c.enabled !== false,
        order: c.order ?? 999,
        locked: lockedTypes ? lockedTypes.includes(d.id) : plans.length > 0 && !plans.includes(currentPlan),
      };
    }).filter((d) => d.enabled).sort((a, b) => a.order - b.order);
  }, [typeCfg, currentPlan, lockedTypes]);

  const featuredDests = useMemo(() => liveDests.filter((d) => d.featured), [liveDests]);
  const restDests = useMemo(() => liveDests.filter((d) => !d.featured), [liveDests]);

  const renderDest = (d) => (
    d.locked ? (
      <button key={d.id} data-testid={`dest-${d.id}`} onClick={() => nav("/app/billing")}
        className="text-left rounded-2xl border border-dashed border-border bg-muted/40 p-4 relative opacity-80 hover:opacity-100 hover:border-primary/40 transition-all">
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5"><Icon name="Lock" className="h-3 w-3" />Upgrade</span>
        <div className="h-10 w-10 rounded-xl bg-muted grid place-items-center mb-2.5"><Icon name={d.icon} className="h-5 w-5 text-muted-foreground" /></div>
        <div className="font-semibold text-sm text-muted-foreground">{d.name}</div>
        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.desc}</div>
        <div className="mt-2 text-[11px] font-semibold text-primary">Upgrade your plan to unlock →</div>
      </button>
    ) : (
      <button key={d.id} data-testid={`dest-${d.id}`} onClick={() => pickType(d.id)} className={`text-left rounded-2xl border bg-card p-4 hover:border-primary hover:-translate-y-0.5 transition-all relative ${d.featured ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`}>
        {d.featured && <span className="absolute top-3 right-3 text-[10px] font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">Priority</span>}
        <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center mb-2.5"><Icon name={d.icon} className="h-5 w-5 text-primary" /></div>
        <div className="font-semibold text-sm">{d.name}</div>
        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.desc}</div>
        <div className="mt-2 flex gap-1">{d.modes.map((m) => <span key={m} className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${m === "static" ? "bg-muted text-muted-foreground" : "bg-emerald-500/10 text-emerald-500"}`}>{m}</span>)}</div>
      </button>
    )
  );

  useEffect(() => {
    if (!id) return;
    api.get(`/qr/${id}`).then(({ data: q }) => {
      setRecord(q); setType(q.destination_type); setMode(q.mode); setVcardMode(q.vcard_mode || "updateable");
      setName(q.name); setData(q.data || {}); setDesign({ ...DEFAULT_DESIGN, ...(q.design || {}) }); setFolder(q.folder || ""); setTagText((q.tags || []).join(", ")); setStep(2);
    }).catch(() => toast.error("Could not load QR"));
  }, [id]);

  const meta = type ? destMeta(type) : null;
  const publicId = record?.public_id || "PREVIEW1";
  const [planInfo, setPlanInfo] = useState(null);
  useEffect(() => { api.get("/company/plan").then(({ data: p }) => setPlanInfo(p)).catch(() => setPlanInfo({ entitlements: { watermark: true } })); }, []);
  // entitlement-based (members never receive the plan name); fails closed to watermarking
  const watermark = planInfo ? !!planInfo.entitlements?.watermark : true;
  const [cdomain, setCdomain] = useState(null);
  useEffect(() => { api.get("/company/domain").then(({ data: d }) => setCdomain(d.usable_hostname)).catch(() => {}); }, []);
  const dynUrl = record?.dynamic_url || (cdomain && type !== "wifi" ? `https://${cdomain}/api/r/${publicId}` : undefined);
  const encoded = useMemo(() => buildEncodedData({ type, mode, data, publicId, vcardMode, dynamicUrl: dynUrl }), [type, mode, data, publicId, vcardMode, dynUrl]);
  // Debounced copy of data → keeps the live QR preview smooth while typing.
  const [dData, setDData] = useState({});
  useEffect(() => { const t = setTimeout(() => setDData(data), 220); return () => clearTimeout(t); }, [data]);
  const previewEncoded = useMemo(() => buildEncodedData({ type, mode, data: dData, publicId, vcardMode, dynamicUrl: dynUrl }), [type, mode, dData, publicId, vcardMode, dynUrl]);
  const toggleDomain = async (use) => {
    try { const { data: r } = await api.post(`/qr/${record.id}/domain`, { use_custom_domain: use }); const { data: q } = await api.get(`/qr/${record.id}`); setRecord(q); toast.success(r.qr_domain ? `New downloads now use ${r.qr_domain}. Already printed codes keep their original link.` : "New downloads use the main qrcodecreatorstudio.com link again."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pickType = (t) => {
    const m = destMeta(t);
    setType(t);
    setData({});
    const nextMode = m.modes.includes("dynamic") ? "dynamic" : m.modes[0];
    setMode(nextMode);
    setStep(m.modes.length > 1 ? 1 : 2);
  };

  const ensureDraft = async () => {
    if (record) return record;
    const { data: q } = await api.post("/qr", { name: name || `${meta.name} QR`, destination_type: type, mode, status: "draft", data, design, vcard_mode: vcardMode, folder, tags });
    setRecord(q);
    return q;
  };

  const goDesign = async () => {
    if (!name.trim()) return toast.error("Give your QR a name");
    try { if (mode === "dynamic") await ensureDraft(); setStep(3); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async (status) => {
    setSaving(true);
    try {
      let q = record;
      const payload = { name: name || `${meta.name} QR`, destination_type: type, mode, status, data, design, vcard_mode: vcardMode, folder, tags };
      if (q) { const { data: up } = await api.put(`/qr/${q.id}`, payload); q = up; }
      else { const { data: cr } = await api.post("/qr", payload); q = cr; }
      setRecord(q);
      toast.success(status === "active" ? "QR published!" : "Draft saved");
      nav("/app");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const savePreset = async () => {
    const nm = prompt("Preset name");
    if (!nm) return;
    try { const { data: p } = await api.post("/presets", { name: nm, design }); setPresets([p, ...presets]); toast.success("Preset saved"); }
    catch { toast.error("Could not save preset"); }
  };

  const addLogoToLibrary = async (logo) => {
    if (!logo) return toast.error("Upload a logo first");
    const lib = [...(company?.logo_library || [])];
    if (!lib.includes(logo)) lib.unshift(logo);
    try { const { data } = await api.put("/company", { logo_library: lib }); setCompany(data); toast.success("Logo saved to company library"); }
    catch { toast.error("Could not save logo"); }
  };

  const doDownload = async (ext) => {
    const tid = toast.loading(`Preparing ${ext.toUpperCase()}…`);
    try {
      await downloadQR(design, encoded, name || "qr-code-creator-studio-qr", ext, watermark);
      toast.success(`${ext.toUpperCase()} downloaded`, { id: tid });
    } catch (e) {
      const m = e?.message || "";
      const msg = m === "scan-failed" ? "This design isn't scannable — raise contrast or the quiet zone" : m.includes("unscannable") ? "Watermark couldn't fit safely — try a simpler design" : "Download failed";
      toast.error(msg, { id: tid });
    }
  };
  const doPrint = async () => {
    const tid = toast.loading("Preparing print…");
    try { await printQR(design, encoded, watermark); toast.success("Opening print…", { id: tid }); }
    catch (e) { toast.error(e?.message === "popup-blocked" ? "Allow pop-ups to print" : "Print failed", { id: tid }); }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => nav("/app")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1"><Icon name="ArrowLeft" className="h-4 w-4" />Back to QR codes</button>
          <h1 className="text-2xl font-display font-extrabold">{id ? "Edit QR code" : "Create a QR code"}</h1>
          {record?.creator && <div className="text-xs text-muted-foreground mt-1" data-testid="qr-created-by">Created by {record.creator.status === "ok" ? `${record.creator.name} (${record.creator.email})` : record.creator.label}{record.created_at ? ` · ${new Date(record.created_at).toLocaleString()}` : ""}{record.updater ? ` · last edited by ${record.updater.name}` : ""}</div>}
          {!can("qr.edit") && <div data-testid="builder-readonly" className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-2 text-xs">View only — your Viewer role can preview and download but not change QR codes.</div>}
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 shrink-0">
            <button disabled={i > step} onClick={() => i <= step && setStep(i)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}><span className="h-5 w-5 rounded-full grid place-items-center bg-black/10">{i < step ? <Icon name="Check" className="h-3 w-3" /> : i + 1}</span>{s}</button>
            {i < STEPS.length - 1 && <Icon name="ChevronRight" className="h-4 w-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step 0: Destination */}
      {step === 0 && (
        <div>
          <h2 className="text-lg font-display font-bold mb-1">Choose your QR code destination</h2>
          <p className="text-sm text-muted-foreground mb-5">Green tags can be static or dynamic. Others are dynamic (hosted) so they stay editable.</p>
          {featuredDests.length > 0 && (
            <div className="mb-6" data-testid="builder-featured-row">
              <div className="flex items-center gap-2 mb-3"><Icon name="Sparkles" className="h-4 w-4 text-primary" /><h3 className="text-sm font-display font-bold">Featured</h3><span className="text-xs text-muted-foreground">Recommended for your workspace</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {featuredDests.map((d) => renderDest(d))}
              </div>
            </div>
          )}
          {featuredDests.length > 0 && <h3 className="text-sm font-display font-bold mb-3">All destinations</h3>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {restDests.map((d) => renderDest(d))}
          </div>
        </div>
      )}

      {/* Step 1: Static vs Dynamic */}
      {step === 1 && meta && (
        <div className="max-w-3xl">
          <h2 className="text-lg font-display font-bold mb-1">Static or dynamic {meta.name}?</h2>
          <p className="text-sm text-muted-foreground mb-5">This choice affects editing and analytics. We never convert silently.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {[{ id: "static", t: "Static", d: "Data lives in the image. Works forever offline, but not editable and no scan analytics.", icon: "Lock", ok: meta.modes.includes("static") }, { id: "dynamic", t: "Dynamic", d: "Short permanent link. Edit anytime, full scan analytics. Needs hosting.", icon: "RefreshCw", ok: meta.modes.includes("dynamic") }].map((o) => (
              <button key={o.id} disabled={!o.ok} data-testid={`mode-${o.id}`} onClick={() => { setMode(o.id); setStep(2); }} className={`text-left rounded-2xl border-2 p-6 transition-colors disabled:opacity-40 ${mode === o.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                <Icon name={o.icon} className="h-6 w-6 text-primary mb-3" />
                <div className="font-display font-bold">{o.t}{!o.ok && <span className="text-xs text-muted-foreground ml-2">(not available)</span>}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{o.d}</p>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(0)} className="mt-5 text-sm text-muted-foreground hover:text-foreground">← Choose a different destination</button>
        </div>
      )}

      {/* Steps 2 & 3: split layout */}
      {step >= 2 && meta && (
        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          <div className="rounded-2xl border border-border bg-card p-5 lg:p-6">
            {step === 2 && (<>
              <div className="flex items-center gap-2 mb-4"><Icon name={meta.icon} className="h-5 w-5 text-primary" /><h2 className="font-display font-bold">{meta.name} details</h2><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ml-auto ${mode === "static" ? "bg-muted" : "bg-emerald-500/10 text-emerald-500"}`}>{mode}</span></div>
              <Field label="QR name (internal)"><Input placeholder="e.g. John — Business card" value={name} onChange={(e) => setName(e.target.value)} data-testid="qr-name" /></Field>
              <div className="h-4" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Folder"><Input placeholder="e.g. Sales team" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="qr-folder" /></Field>
                <Field label="Tags (comma separated)"><Input placeholder="dubai, marketing" value={tagText} onChange={(e) => setTagText(e.target.value)} data-testid="qr-tags" /></Field>
              </div>
              <div className="h-4" />
              {type === "vcard" ? <VCardForm data={data} onChange={setData} vcardMode={vcardMode} setVcardMode={setVcardMode} /> : <DestinationForm type={type} data={data} onChange={setData} record={record} design={design} company={company} />}
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(meta.modes.length > 1 ? 1 : 0)} className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent">Back</button>
                <button data-testid="to-design" onClick={goDesign} className="flex-1 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90 flex items-center justify-center gap-2">Continue to design <Icon name="ArrowRight" className="h-4 w-4" /></button>
              </div>
            </>)}
            {step === 3 && (<>
              <h2 className="font-display font-bold mb-4 flex items-center gap-2"><Icon name="Palette" className="h-5 w-5 text-primary" />Design studio</h2>
              <DesignStudio design={design} onChange={setDesign} dataLength={encoded.length} onSavePreset={savePreset} presets={presets} companyLogos={company?.logo_library || []} onLogoToLibrary={addLogoToLibrary} />
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(2)} className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent">Back</button>
                <button data-testid="save-draft" disabled={saving} onClick={() => save("draft")} className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent">Save draft</button>
                <button data-testid="publish-qr" disabled={saving} onClick={() => save("active")} className="flex-1 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90 flex items-center justify-center gap-2"><Icon name="Rocket" className="h-4 w-4" />Publish</button>
              </div>
            </>)}
          </div>

          {/* Preview panel */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 sticky top-20">
              <div className="flex gap-1 mb-4 rounded-xl bg-muted p-1">
                {[["qr", "QR"], ["phone", "Scan result"], ["card", "Business card"]].map(([k, l]) => (
                  <button key={k} data-testid={`preview-${k}`} onClick={() => setPreview(k)} className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors ${preview === k ? "bg-background shadow-sm" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
              {preview === "qr" && (
                <div className="space-y-4">
                  <div className="rounded-xl bg-white p-4 grid place-items-center"><div style={{ width: 240 }}><QRPreview design={design} data={previewEncoded} watermark={watermark} /></div></div>
                  {mode === "dynamic" && record && <div className="text-xs text-muted-foreground break-all bg-muted rounded-lg p-2 font-mono" data-testid="qr-dynamic-url">{record.dynamic_url}</div>}
                  {mode === "dynamic" && record && type !== "wifi" && can("qr.edit") && (cdomain || record.qr_domain) && (
                    <div className="text-xs rounded-lg border border-border p-2.5 space-y-1.5" data-testid="qr-domain-panel">
                      {record.qr_domain ? <><div>This QR uses your custom domain <b>{record.qr_domain}</b>. The main link {record.main_url} still works for codes printed earlier.</div><button data-testid="qr-domain-off" onClick={() => toggleDomain(false)} className="underline">Use main domain for new downloads</button></>
                        : <><div>Previously printed copies contain the original qrcodecreatorstudio.com link and keep working. Reprint to show your domain.</div><button data-testid="qr-domain-on" onClick={() => toggleDomain(true)} className="rounded-md bg-primary text-primary-foreground px-2.5 py-1 font-semibold">Use custom domain for new downloads</button></>}
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    <button data-testid="download-png" onClick={() => doDownload("png")} className="rounded-xl border border-border py-2 text-xs font-bold hover:bg-accent flex flex-col items-center gap-1"><Icon name="Download" className="h-4 w-4" />PNG</button>
                    <button data-testid="download-jpg" onClick={() => doDownload("jpg")} className="rounded-xl border border-border py-2 text-xs font-bold hover:bg-accent flex flex-col items-center gap-1"><Icon name="Image" className="h-4 w-4" />JPG</button>
                    <button data-testid="download-svg" onClick={() => doDownload("svg")} className="rounded-xl border border-border py-2 text-xs font-bold hover:bg-accent flex flex-col items-center gap-1"><Icon name="Code" className="h-4 w-4" />SVG</button>
                    <button data-testid="download-pdf" onClick={doPrint} className="rounded-xl border border-border py-2 text-xs font-bold hover:bg-accent flex flex-col items-center gap-1"><Icon name="Printer" className="h-4 w-4" />Print</button>
                  </div>
                </div>
              )}
              {preview === "phone" && <div className="scale-90 origin-top"><PhoneMockup><ScanResult type={type} data={data} company={{ name: data.company }} live /></PhoneMockup></div>}
              {preview === "card" && (
                <div className="rounded-xl bg-white text-neutral-900 p-5 aspect-[1.75/1] flex items-center gap-4 shadow-inner">
                  <div style={{ width: 96 }}><QRPreview design={{ ...design, frame: "none" }} data={encoded} /></div>
                  <div className="min-w-0">
                    <div className="font-display font-extrabold text-sm truncate">{[data.prefix, data.first_name, data.last_name].filter(Boolean).join(" ") || name || "Your Name"}</div>
                    <div className="text-xs text-neutral-500 truncate">{data.job_title || meta.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{data.company || "Company"}</div>
                    <div className="text-[10px] text-neutral-400 mt-1 truncate">{data.mobile || data.email || ""}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
